/**
 * Bee Swarm Positioning Algorithm
 *
 * Creates a symmetric "swarm" distribution like a violin plot or sideways histogram.
 * Dots pack organically around the center line, bulging where density is high.
 * @remarks
 * Algorithm (inspired by d3-beeswarm):
 * 1. Sort values by x-position
 * 2. For each dot, find the y-position closest to 0 that doesn't overlap existing dots
 * 3. Check positions spiraling outward from center: 0, +step, -step, +2*step, -2*step...
 * 4. Use true Euclidean distance for collision detection (circle packing)
 *
 * This approach ensures:
 * - Symmetric spread above and below the axis
 * - Organic, rounded-sm silhouette (not stepped/grid-like)
 * - Dots nestle into gaps naturally
 * - Dense areas bulge, sparse areas stay flat
 *
 * Performance: The placement loop uses a mutable local array to avoid O(n^2)
 * array copies that occur with reduce-and-spread. The function remains pure --
 * it returns a new readonly array and never mutates its inputs.
 */

/**
 * Input data for bee swarm positioning - score value with optional player info
 */
export interface BeeSwarmInput {
  /** Score value (0-100) */
  readonly value: number;
  /** Optional player name for tooltip display */
  readonly playerName?: string;
  /** Optional player ID */
  readonly playerId?: string;
}

/**
 * Positioned dot with calculated y-offset
 */
export interface DotPosition {
  /** X-position as percentage (0-100) */
  readonly x: number;
  /** Vertical offset from center line in pixels (negative = up, positive = down) */
  readonly y: number;
  /** Original value for key generation and tracking */
  readonly value: number;
  /** Index of original dot for stable key generation */
  readonly originalIndex: number;
  /** Optional player name for tooltip display */
  readonly playerName?: string;
  /** Optional player ID */
  readonly playerId?: string;
}

/**
 * Configuration for bee swarm algorithm
 */
export interface BeeSwarmConfig {
  /** Radius of each dot in pixels (default: 3) */
  readonly dotRadius: number;
  /** Minimum spacing between dot edges in pixels (default: 1) */
  readonly minSpacing: number;
  /** Maximum vertical displacement from center in pixels (default: 28) */
  readonly maxYOffset: number;
  /** Maximum score value for x-axis scaling (default: 100) */
  readonly maxValue: number;
}

/**
 * Default maximum score value for x-axis scaling
 */
const DEFAULT_MAX_VALUE = 100;

/**
 * Default bee swarm configuration
 * Tuned for 300+ player distributions across 0-100 score range
 */
const DEFAULT_CONFIG: Readonly<BeeSwarmConfig> = {
  dotRadius: 3,
  minSpacing: 1,
  maxYOffset: 28,
  maxValue: DEFAULT_MAX_VALUE,
};

/**
 * Clamp a value to the 0-100 percentage range
 * @param value - Value to clamp
 * @returns Value clamped between 0 and 100
 */
const clampToPercentage = (value: number): number =>
  Math.max(0, Math.min(100, value));

/**
 * Check if two circles overlap using Euclidean distance
 * @param x1 - First circle x position (percentage)
 * @param y1 - First circle y position (pixels)
 * @param x2 - Second circle x position (percentage)
 * @param y2 - Second circle y position (pixels)
 * @param minDist - Minimum distance between centers
 * @param xScale - Scale factor to convert x% to pixels (container width / 100)
 * @returns True if circles overlap
 */
const circlesOverlap = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  minDist: number,
  xScale: number
): boolean => {
  const dx = (x1 - x2) * xScale;
  const dy = y1 - y2;
  const distSq = dx * dx + dy * dy;
  return distSq < minDist * minDist;
};

/**
 * Build symmetric spiral positions: 0, +step, -step, +2*step, -2*step, ...
 * This ensures perfect 50/50 distribution above and below the axis.
 * @param maxOffset - Maximum y offset
 * @param step - Step size between positions
 * @returns Array of y positions in spiral order
 */
const buildSpiralPositions = (maxOffset: number, step: number): number[] => {
  const maxSteps = Math.ceil(maxOffset / step);

  return Array.from({ length: maxSteps }, (_, i) => i + 1).reduce<number[]>(
    (acc, i) => {
      const yDown = i * step; // Positive = below center line
      const yUp = -i * step; // Negative = above center line

      // Build new positions array immutably using conditional spread
      const newPositions = [
        ...(yDown <= maxOffset ? [yDown] : []),
        ...(Math.abs(yUp) <= maxOffset ? [yUp] : []),
      ];

      return [...acc, ...newPositions];
    },
    [0] // Start with center position
  );
};

/**
 * Find the y-position closest to 0 that doesn't overlap any existing dots
 * Searches outward from center: 0, +step, -step, +2*step, -2*step, etc.
 * @param xPercent - X position as percentage (0-100)
 * @param placedDots - Already positioned dots
 * @param config - Bee swarm configuration
 * @param xScale - Scale factor for x-axis (pixels per percentage point)
 * @returns Y-offset in pixels that avoids collision
 */
const findClosestAvailableY = (
  xPercent: number,
  placedDots: readonly DotPosition[],
  config: Readonly<BeeSwarmConfig>,
  xScale: number
): number => {
  const minDist = config.dotRadius * 2 + config.minSpacing;
  const step = config.dotRadius; // Small step for smooth placement

  // Check if a y-position is free (no overlap with any placed dot)
  const isFree = (y: number): boolean =>
    !placedDots.some(dot =>
      circlesOverlap(xPercent, y, dot.x, dot.y, minDist, xScale)
    );

  // Build symmetric spiral: 0, +step, -step, +2*step, -2*step, ...
  const spiralPositions = buildSpiralPositions(config.maxYOffset, step);

  // Find first available position in spiral order
  const foundY = spiralPositions.find(y => isFree(y));

  // Return found position or clamp to max
  return foundY ?? config.maxYOffset;
};

/**
 * Normalize input to BeeSwarmInput array
 * Accepts either raw numbers or BeeSwarmInput objects for backwards compatibility
 * @param input - Array of numbers or BeeSwarmInput objects
 * @returns Normalized array of BeeSwarmInput objects
 */
const normalizeInput = (
  input: readonly (number | BeeSwarmInput)[]
): readonly BeeSwarmInput[] =>
  input.map(item => (typeof item === "number" ? { value: item } : item));

/**
 * Calculate bee swarm positions for distribution dots
 *
 * Creates a symmetric violin-plot-like distribution where dots pack
 * organically around the center line. Dense areas bulge outward.
 *
 * Uses a mutable local accumulator internally to avoid O(n^2) array copies
 * that occur with reduce-and-spread on 300+ dots. The function is externally
 * pure -- it never mutates its inputs and returns a new readonly array.
 * @param values - Array of percentage values (0-100) or BeeSwarmInput objects with player info
 * @param partialConfig - Optional partial configuration (merged with defaults)
 * @param containerWidth - Width of container in pixels (default: 400)
 * @returns Array of positioned dots with y-offsets, sorted by x-position
 */
export function calculateBeeSwarmPositions(
  values: readonly (number | BeeSwarmInput)[],
  partialConfig?: Partial<Readonly<BeeSwarmConfig>>,
  containerWidth: number = 400
): readonly DotPosition[] {
  const config = { ...DEFAULT_CONFIG, ...partialConfig };

  // Normalize input to BeeSwarmInput array
  const normalizedValues = normalizeInput(values);

  // Empty array case
  if (normalizedValues.length === 0) {
    return [];
  }

  // Single value case - no offset needed
  if (normalizedValues.length === 1) {
    const item = normalizedValues[0];
    const clampedValue = clampToPercentage(
      (item.value / config.maxValue) * 100
    );
    return [
      {
        x: clampedValue,
        y: 0,
        value: item.value,
        originalIndex: 0,
        playerName: item.playerName,
        playerId: item.playerId,
      },
    ];
  }

  // Scale factor: how many pixels per percentage point
  const xScale = containerWidth / 100;

  // Create indexed values for tracking original order, scaled to 0-100% range
  const indexedValues = normalizedValues.map((item, index) => ({
    value: item.value,
    playerName: item.playerName,
    playerId: item.playerId,
    index,
    clampedX: clampToPercentage((item.value / config.maxValue) * 100),
  }));

  // Sort by clamped x-position for sequential placement (beeswarm works best sorted)
  const sortedIndexedValues = [...indexedValues].sort(
    (a, b) => a.clampedX - b.clampedX
  );

  /* Mutable local accumulator: avoids creating a new array copy on every
     iteration (O(n^2) allocation with reduce-and-spread). The array never
     escapes this function scope -- callers receive a readonly DotPosition[]. */
  const placedDots: DotPosition[] = [];

  return sortedIndexedValues.map(
    ({ value, playerName, playerId, index, clampedX }) => {
      const yOffset = findClosestAvailableY(
        clampedX,
        placedDots,
        config,
        xScale
      );

      const dot: DotPosition = {
        x: clampedX,
        y: yOffset,
        value,
        originalIndex: index,
        playerName,
        playerId,
      };

      // eslint-disable-next-line functional/immutable-data -- O(n) local accumulator vs O(n^2) reduce-and-spread for 300+ dots per metric
      placedDots.push(dot);
      return dot;
    }
  );
}
